import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { filesAPIService } from '~/api/files';
import { qrcodeAPIService } from '~/api/qrcode';
import QRCodeCorrectionLevel from '~/components/QRCodeDesign/QRCodeCorrectionLevel';
import QRCodeFrame from '~/components/QRCodeDesign/QRCodeFrame';
import QRCodeLogo from '~/components/QRCodeDesign/QRCodeLogo/QRCodeLogo';
import QRCodePattern from '~/components/QRCodeDesign/QRCodePattern/QRCodePattern';
import Tabs from '~/components/ui/Tabs';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { qrCodeKeys } from '~/constants/queryKeys';
import useQrCodeStylesData from '~/hooks/useQrCodeStylesData';
import { useAppDispatch } from '~/hooks/useRedux';
import { useQrCodeById } from '~/queries/qrCode';
import { setIsQrCodeStylesUpdating } from '~/store/reducers/anonQr/slice';
import { COLORS } from '~/styles/colors';
import { QrCodeLogoState } from '~/types/builder';
import { QrCodeCorrectionLevel, QrCodeFrameItem, QrCodeStyle } from '~/types/qrCode';
import { getFileFromLocalSource } from '~/utils/common';

type AnonymousDesignSectionProps = {
  qrCodeId?: number;
  updateQrCodeMutate?: Function;
  qrCodeLoading?: boolean;
  qrCodeData?: any;
};
export default function AnonymousDesignSection({ qrCodeId }: AnonymousDesignSectionProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));
  const styles = useStyles();

  const [selectedLogo, setSelectedLogo] = useState<QrCodeLogoState>({ name: 'empty', file: null });
  const [tabsValue, setTabsValue] = useState(0);

  const { data: qrCodeStyles, isLoading } = useQrCodeStylesData();
  const { data: qrCodeData } = useQrCodeById({ qrCodeId, select: (res) => res.data });

  const qrCodeDataStylesRef = useRef(qrCodeData?.style);

  const { mutate: updateQrCodeMutate } = useMutation({
    mutationFn: (body) => qrcodeAPIService.updateQrCode(body, qrCodeId),
    onMutate: () => {
      dispatch(setIsQrCodeStylesUpdating(true));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qrCodeKeys.qrCodeData });
      dispatch(setIsQrCodeStylesUpdating(false));
    },
  });

  const { mutate: uploadImage } = useMutation({
    mutationFn: filesAPIService.uploadImage,
    onSuccess: (response) => {
      updateQrCodeMutate({
        embedded_logo_id: response.data,
      });
    },
  });

  useEffect(() => {
    if (qrCodeData?.style) {
      qrCodeDataStylesRef.current = qrCodeData.style;
    }
  }, [qrCodeData]);

  const handleChangeTab = (_, newValue) => {
    setTabsValue(newValue);
  };

  const handleChangeFrame = (newFrame: QrCodeFrameItem) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      frame: {
        ...qrCodeDataStylesRef.current?.frame,
        style: newFrame.id,
        phrase_color: newFrame.defaultTextColor,
      },
    };

    qrCodeDataStylesRef.current = newStyle;

    updateQrCodeMutate({
      style: newStyle,
    });
  };

  const handleChangeShape = (newShape: string) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      shape: {
        ...qrCodeDataStylesRef.current?.shape,
        style: newShape,
      },
    };

    qrCodeDataStylesRef.current = newStyle;

    updateQrCodeMutate({
      style: newStyle,
    });
  };

  const handleChangeLevel = (newLevel: QrCodeCorrectionLevel) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      error_correction_level: newLevel,
    };

    qrCodeDataStylesRef.current = newStyle;

    updateQrCodeMutate({
      style: newStyle,
    });
  };

  const handleChangeLogo = async (newLogo: QrCodeLogoState) => {
    if (newLogo.name === 'empty') {
      void handleDeleteUploadedLogo();
    } else {
      setSelectedLogo(newLogo);
      const fileURL = await getFileFromLocalSource(window.URL.createObjectURL(newLogo.file));
      uploadImage(fileURL);
    }
  };

  const handleDeleteUploadedLogo = async () => {
    setSelectedLogo({ name: 'empty', file: null });

    updateQrCodeMutate({
      embedded_logo_id: null,
    });
  };

  const handleUpdateQrStyles = (newStyles: QrCodeStyle) => {
    qrCodeDataStylesRef.current = newStyles;
    updateQrCodeMutate({
      style: newStyles,
    });
  };

  const tabs = [
    { label: t('thirdStep:frame') },
    { label: t('builder:designStep.pattern.title') },
    { label: t('thirdStep:correctionLevel') },
    { label: t('builder:designStep.logo.title') },
  ];

  if (isDesktop) {
    return isLoading ? (
      <StyledLoadingContainer>
        <StyledCircularProgress />
      </StyledLoadingContainer>
    ) : (
      <>
        <Box sx={styles.desktopTabs}>
          {tabs.map((tab, index) => {
            return (
              <ButtonBase
                key={tab.label}
                onClick={() => setTabsValue(index)}
                sx={styles.desktopTab}
                className={tabsValue === index ? 'selected' : undefined}
              >
                {tab.label}
              </ButtonBase>
            );
          })}
        </Box>

        {tabsValue === 0 && (
          <QRCodeFrame
            frames={qrCodeStyles?.frames}
            defaultSelectedFrame={qrCodeData?.style?.frame?.style}
            onSelectFrame={handleChangeFrame}
            qrStyles={qrCodeData?.style}
            onUpdateQrStyles={handleUpdateQrStyles}
            defaultExpanded
          />
        )}
        {tabsValue === 1 && (
          <QRCodePattern
            onSelectShape={handleChangeShape}
            defaultSelectedShape={qrCodeData?.style?.shape?.style}
            defaultExpanded
          />
        )}
        {tabsValue === 2 && (
          <QRCodeCorrectionLevel
            levels={qrCodeStyles?.errorCorrectionLevels}
            onSelectLevel={handleChangeLevel}
            defaultSelectedLevel={qrCodeData?.style?.errorCorrectionLevel}
            defaultExpanded
          />
        )}
        {tabsValue === 3 && (
          <QRCodeLogo
            selectedLogo={selectedLogo}
            onSelectLogo={handleChangeLogo}
            onDeleteUploadedLogo={handleDeleteUploadedLogo}
            defaultExpanded
          />
        )}
      </>
    );
  }

  return (
    <>
      {isLoading ? (
        <StyledLoadingContainer>
          <StyledCircularProgress />
        </StyledLoadingContainer>
      ) : (
        <>
          <Tabs
            value={tabsValue}
            onChange={handleChangeTab}
            tabs={tabs}
            scrollButtons="auto"
            sx={{ height: 'auto', '& .MuiTabs-flexContainer': { overflowX: 'auto' } }}
          />
          {tabsValue === 0 && (
            <QRCodeFrame
              frames={qrCodeStyles?.frames}
              defaultSelectedFrame={qrCodeData?.style?.frame?.style}
              onSelectFrame={handleChangeFrame}
              qrStyles={qrCodeData?.style}
              onUpdateQrStyles={handleUpdateQrStyles}
              defaultExpanded
            />
          )}
          {tabsValue === 1 && (
            <QRCodePattern
              onSelectShape={handleChangeShape}
              defaultSelectedShape={qrCodeData?.style?.shape?.style}
              defaultExpanded
            />
          )}
          {tabsValue === 2 && (
            <QRCodeCorrectionLevel
              levels={qrCodeStyles?.errorCorrectionLevels}
              onSelectLevel={handleChangeLevel}
              defaultSelectedLevel={qrCodeData?.style?.errorCorrectionLevel}
              defaultExpanded
            />
          )}
          {tabsValue === 3 && (
            <QRCodeLogo
              selectedLogo={selectedLogo}
              onSelectLogo={handleChangeLogo}
              onDeleteUploadedLogo={handleDeleteUploadedLogo}
              defaultExpanded
            />
          )}
        </>
      )}
    </>
  );
}

export const StyledLoadingContainer = styled('div')(() => {
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  };
});

export const StyledCircularProgress = styled(CircularProgress)(() => {
  return {
    circle: {
      stroke: COLORS.primary,
    },
  };
});

function useStyles() {
  return {
    desktopTabs: css`
      width: 100%;
      border-radius: 8px;
      display: flex;
      padding: 12px;
      gap: 8px;
      border: 1px solid ${COLORS.grey200};
      background-color: ${COLORS.white};
    `,
    desktopTab: css`
      border-radius: 8px;
      flex: 1;
      cursor: pointer;
      border: 1px solid ${COLORS.grey200};
      color: ${COLORS.grey200};
      font-size: 14px;
      font-weight: 500;
      padding: 4px 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 19px;
      background: none;

      &.selected {
        border-color: ${COLORS.primary};
        color: ${COLORS.primary};
        background-color: ${COLORS.blue50};
      }
    `,
  };
}
